import {useQuery} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {Plus} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Icon} from '@/components/shared/Icon'
import {permissions} from '@/lib/permissions'
import {useEmbeddedListProps} from '@/hooks/use-embedded-list-props'
import {FunctionGroupControllerListEndpoint} from '@/types/endpoint-contracts'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {routes} from '@/lib/routes'
import {Badge} from '@/components/ui/badge'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {CONCEPT_ICON} from '@/lib/concepts'

/**
 * List page for function groups (issue 745). Operators see all function
 * groups and can create new ones from here when they hold
 * {@code function-groups:create}.
 */
interface FunctionGroupsProps {
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

export function FunctionGroups({embedded, ownedByMe, emptyStateTitle}: FunctionGroupsProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {functionGroupTypes} = useAppConfig()
    usePageTitle(i18n.functionGroups.title)

    const {canCreate, title, emptyMessage} = useEmbeddedListProps({
        contract: FunctionGroupControllerListEndpoint,
        ownedByMe,
        baseQueryString: '',
        emptyStateTitle,
        defaultTitle: i18n.functionGroups.title,
        createPermission: permissions.functionGroups.create,
    })

    const {data} = useQuery({
        queryKey: ['function-groups', 'list-page', ownedByMe],
        queryFn: () => services.functionGroups.list({size: 50, ...(ownedByMe ? {ownedByMe: true} : {})}),
    })
    const groups = data?.content ?? []
    const totalItems = data?.totalElements ?? 0

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON['function-group']}
            title={title}
            count={totalItems}
            description={i18n.functionGroups.subtitle}
            data-testid="function-groups-list"
            embedded={embedded}
            actions={canCreate ? (
                <Button
                    size="sm"
                    onClick={() => navigate(routes.accessGovernance.functionGroups.create())}
                    data-testid="btn-new-function-group"
                >
                    <Icon icon={Plus} className="mr-1"/>
                    {i18n.functionGroups.createFunctionGroup}
                </Button>
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                        <TableHead data-testid="col-owner">{i18n.functionGroups.owner}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {groups.map((fg) => (
                        <ClickableTableRow key={fg.id} to={routes.accessGovernance.functionGroups.detail(fg.id)}>
                            <TableCell className="font-medium">
                                <TextLink
                                    to={routes.accessGovernance.functionGroups.detail(fg.id)}
                                    testId={`link-fg-detail-${fg.id}`}
                                    stopPropagation
                                >
                                    {fg.name}
                                </TextLink>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {labelOf(functionGroupTypes, fg.functionGroupType)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {fg.owners && fg.owners.length > 0
                                    ? fg.owners.map((o) => o.ownerPartyName).join(', ')
                                    : '—'}
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {groups.length === 0 && (
                        <TableEmptyState
                            colSpan={3}
                            hasFilters={false}
                            matchMessage={i18n.functionGroups.noFunctionGroupsMatch}
                            emptyMessage={emptyMessage ?? i18n.functionGroups.noFunctionGroupsFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
